import { useState, useEffect, ChangeEvent, JSX } from 'react';
import dayjs from 'dayjs';
import minMax from 'dayjs/plugin/minMax';

import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';

import useFormState from '../hooks/useFormState';
import DatePickerField from '../fields/DatePickerField';
import OutlinedTextField from '../fields/OutlinedTextField';
import Loader from '../Loader';
import { notify } from '../Notification';
import ConfirmDialog from '../ConfirmDialog';
import {
  Coverage,
  CoverageMember,
  EditEnrollment,
  EditEnrollmentsDialogProps,
  EditEnrollmentsPayload,
  EnrollmentStates,
  EnrollmentState,
  Plan,
} from './types';
import StandardTable, { makeHeader, makeRow } from '../StandardTable';
import { formatDate, formatMemberId, isValidDate } from '../utilities/index';
import { createCoverageMembers } from './EmployeeCoverageTable';
import TableButton from '../TableButton';

dayjs.extend(minMax);
const NoWrap = styled(Typography)({ display: 'inline', whiteSpace: 'nowrap', fontSize: 'inherit' });

export default function EditEnrollmentsDialog({
  open,
  onClose,
  planYear,
  coverage,
  bulkEnrollEmployee,
  showLoader,
  getPlans,
}: EditEnrollmentsDialogProps) {
  const [dentalPlans, setDentalPlans] = useState<Plan[]>([]);
  const [visionPlans, setVisionPlans] = useState<Plan[]>([]);
  const [showVisionPart, setShowVisionPart] = useState(false);
  const [cloneDentalToVisionSettings, setCloneDentalToVisionSettings] = useState(false);
  const { formState, setFormState } = useFormState<EditEnrollmentsPayload | null>(null);

  useEffect(() => {
    if (open && planYear && coverage) {
      setShowVisionPart(Boolean(coverage.primary_coverage_vision));
      setCloneDentalToVisionSettings(coverage.dental_and_vision_coverages_match);
      getPlans(planYear)
        .then((response) => {
          setDentalPlans(response.filter((plan: Plan) => plan.type !== 'vision'));
          setVisionPlans(response.filter((plan: Plan) => plan.type === 'vision'));
        })
        .catch((error) => {
          notify(error.message, 'error');
        });
    }
  }, [open, planYear, coverage]);

  useEffect(() => {
    if (open && coverage && dentalPlans) {
      const isSingleDentalPlan = dentalPlans.length === 1;
      const primaryMemberInitiallyHasPlan =
        coverage.primary_coverage_dental?.plan_id || coverage.primary_coverage_vision?.plan_id;

      const createEditEnrollment = (
        cov: Coverage,
        vision: boolean,
        isPrimary: boolean
      ): EditEnrollment => {
        const planId = () => {
          if (cov?.plan_id) return cov.plan_id;
          if (vision) {
            return isSingleDentalPlan && !primaryMemberInitiallyHasPlan ? visionPlans[0]?.id : null;
          }
          return isSingleDentalPlan && !primaryMemberInitiallyHasPlan ? dentalPlans[0]?.id : null;
        };

        const planType = () => {
          if (cov?.plan_type) return cov?.plan_type;
          if (vision) {
            return isSingleDentalPlan && !primaryMemberInitiallyHasPlan
              ? visionPlans[0]?.type
              : null;
          }
          return isSingleDentalPlan && !primaryMemberInitiallyHasPlan ? dentalPlans[0]?.type : null;
        };

        const initialPlanState = () => {
          switch (cov?.coverage_status) {
            case 'pending':
              return EnrollmentStates.Enrolled;
            case 'activated':
              return !cov?.coverage_end_date
                ? EnrollmentStates.Covered1
                : EnrollmentStates.Covered2;
            default:
              return !planId() ? EnrollmentStates.NotEnrolled : EnrollmentStates.Enrolled;
          }
        };

        const payload: EditEnrollment = {
          cobra_id: cov?.cobra_id ?? null,
          coverage_id: cov?.coverage_id ?? null,
          coverage_start_date: cov?.coverage_start_date ?? null,
          coverage_end_date: cov?.coverage_end_date ?? null,
          coverage_type: cov?.coverage_type ?? null,
          family_profile_id: cov?.family_profile_id ?? null,
          is_cobra: cov?.is_cobra,
          plan_id: planId(),
          plan_type: planType(),
          plan_state: initialPlanState(),
        };
        if (isPrimary) {
          payload.employee_id = cov?.employee_id ?? null;
        }
        return payload;
      };

      const createFamilyEditEnrollments = (
        familyCoverages: Coverage[],
        vision: boolean
      ): EditEnrollment[] => {
        if (!familyCoverages) return [];
        const resultEditEnrollments: EditEnrollment[] = [];
        familyCoverages.forEach((familyCoverage: Coverage) => {
          resultEditEnrollments.push(createEditEnrollment(familyCoverage, vision, false));
        });
        return resultEditEnrollments;
      };

      const editEnrollmentsPayload: EditEnrollmentsPayload = {
        primary_coverage_dental: createEditEnrollment(
          coverage.primary_coverage_dental,
          false,
          true
        ),
        family_coverages_dental: createFamilyEditEnrollments(
          coverage.family_coverages_dental,
          false
        ),
        primary_coverage_vision: createEditEnrollment(coverage.primary_coverage_vision, true, true),
        family_coverages_vision: createFamilyEditEnrollments(
          coverage.family_coverages_vision,
          true
        ),
        plan_year: planYear?.id || '',
      };

      setFormState(editEnrollmentsPayload);
    }
  }, [open, coverage, setFormState, dentalPlans]);

  const fieldsToCopy = (dentalCoverage: EditEnrollment) => ({
    plan_id: dentalCoverage.plan_id ? visionPlans[0].id : null,
    plan_type: dentalCoverage.plan_id ? visionPlans[0].type : null,
    plan_state: dentalCoverage.plan_state,
    coverage_start_date: dentalCoverage.coverage_start_date,
    coverage_start_date_error: dentalCoverage.coverage_start_date_error,
    coverage_end_date: dentalCoverage.coverage_end_date,
    coverage_end_date_error: dentalCoverage.coverage_end_date_error,
  });

  const copyPrimaryDentalToVisionState = () => {
    const primaryDentalCoverage = formState?.primary_coverage_dental;
    if (primaryDentalCoverage) {
      setFormState({
        primary_coverage_vision: {
          ...formState?.primary_coverage_vision,
          ...fieldsToCopy(primaryDentalCoverage),
        },
      });
    }
  };

  const copyFamilyDentalToVisionState = () => {
    const resultCoverages = [...formState?.family_coverages_vision!];
    formState?.family_coverages_dental?.forEach((familyCoverage: EditEnrollment, index: number) => {
      resultCoverages.splice(index, 1, {
        ...formState?.family_coverages_vision![index],
        ...fieldsToCopy(familyCoverage as unknown as EditEnrollment),
      });
    });
    setFormState({
      family_coverages_vision: resultCoverages,
    });
  };

  function canSubmit() {
    let canSubmit = true;
    const INVALID_DATE = 'Invalid date';
    // possible errors in the form:
    //    - plan is selected and coverage start date is not defined
    //    - plan_state is EnrollmentStates.Covered2 and coverage end date is not defined

    const newFormState: EditEnrollmentsPayload = {};
    const newPrimaryDentalFormState = { ...formState?.primary_coverage_dental! };
    if (
      formState?.primary_coverage_dental?.plan_id &&
      !isValidDate(formState.primary_coverage_dental.coverage_start_date)
    ) {
      newPrimaryDentalFormState.coverage_start_date_error = INVALID_DATE;
      newFormState.primary_coverage_dental = newPrimaryDentalFormState;
    }
    if (
      formState?.primary_coverage_dental?.plan_state === EnrollmentStates.Covered2 &&
      !isValidDate(formState?.primary_coverage_dental?.coverage_end_date)
    ) {
      newPrimaryDentalFormState.coverage_end_date_error = INVALID_DATE;
      newFormState.primary_coverage_dental = newPrimaryDentalFormState;
    }
    if (formState?.family_coverages_dental) {
      let shouldUpdateFamilyFormState = false;
      const newFamilyDentalFormState = [...formState?.family_coverages_dental];
      formState?.family_coverages_dental?.forEach(
        (familyCoverage: EditEnrollment, index: number) => {
          if (familyCoverage.plan_id && !isValidDate(familyCoverage.coverage_start_date)) {
            newFamilyDentalFormState[index].coverage_start_date_error = INVALID_DATE;
            shouldUpdateFamilyFormState = true;
          }
          if (
            familyCoverage.plan_state === EnrollmentStates.Covered2 &&
            !familyCoverage.coverage_end_date
          ) {
            newFamilyDentalFormState[index].coverage_end_date_error = INVALID_DATE;
            shouldUpdateFamilyFormState = true;
          }
        }
      );
      if (shouldUpdateFamilyFormState) {
        newFormState.family_coverages_dental = newFamilyDentalFormState;
      }
    }

    if (visionPlans?.length === 1 && !cloneDentalToVisionSettings) {
      const newPrimaryVisionFormState = { ...formState?.primary_coverage_vision! };
      if (
        formState?.primary_coverage_vision?.plan_id &&
        !isValidDate(formState.primary_coverage_vision.coverage_start_date)
      ) {
        newPrimaryVisionFormState.coverage_start_date_error = INVALID_DATE;
        newFormState.primary_coverage_vision = newPrimaryVisionFormState;
      }
      if (
        formState?.primary_coverage_vision?.plan_state === EnrollmentStates.Covered2 &&
        !isValidDate(formState?.primary_coverage_vision?.coverage_end_date)
      ) {
        newPrimaryVisionFormState.coverage_end_date_error = INVALID_DATE;
        newFormState.primary_coverage_vision = newPrimaryVisionFormState;
      }
      if (formState?.family_coverages_vision) {
        let shouldUpdateFormState = false;
        const newFamilyVisionFormState = [...formState.family_coverages_vision];
        formState.family_coverages_vision.forEach(
          (familyCoverage: EditEnrollment, index: number) => {
            if (familyCoverage.plan_id && !isValidDate(familyCoverage.coverage_start_date)) {
              newFamilyVisionFormState[index].coverage_start_date_error = INVALID_DATE;
              shouldUpdateFormState = true;
            }
            if (
              familyCoverage.plan_state === EnrollmentStates.Covered2 &&
              !familyCoverage.coverage_end_date
            ) {
              newFamilyVisionFormState[index].coverage_end_date_error = INVALID_DATE;
              shouldUpdateFormState = true;
            }
          }
        );
        if (shouldUpdateFormState) {
          newFormState.family_coverages_vision = newFamilyVisionFormState;
        }
      }
    }

    if (Object.keys(newFormState).length) {
      canSubmit = false;
      setFormState(newFormState);
    }

    return canSubmit;
  }

  const displayForm = () => {
    if (!coverage) return;

    const members: CoverageMember[] = createCoverageMembers(coverage);

    const NO_DENTAL_TEXT = 'No dental';
    const NO_DENTAL_VALUE = 'no-dental';
    const headersList = ['Name', 'Relation', 'Member ID', 'Dental plan', 'Dental status'];
    if (showVisionPart) {
      headersList.splice(5, 0, 'Vision coverage', 'Vision status');
    }
    const headers = makeHeader('coverage-table-header', headersList);

    const rows = members.map((member, index) => {
      const formStateDentalRow = (index: number) => {
        if (!formState) return undefined;
        return index === 0
          ? formState.primary_coverage_dental
          : Boolean(formState.family_coverages_dental?.length)
            ? formState.family_coverages_dental![index - 1]
            : undefined;
      };

      const formStateVisionRow = (index: number) => {
        if (!formState) return undefined;
        return index === 0
          ? formState.primary_coverage_vision
          : Boolean(formState.family_coverages_vision?.length)
            ? formState.family_coverages_vision![index - 1]
            : undefined;
      };

      const planText = () => {
        const currentPlanId = formStateDentalRow(index)?.plan_id;
        const primaryPlanId = formStateDentalRow(0)?.plan_id;
        if (!primaryPlanId && !currentPlanId) {
          return NO_DENTAL_TEXT;
        }
        const planText = dentalPlans.find((plan: Plan) => plan.id == primaryPlanId)?.label;
        return planText ?? NO_DENTAL_TEXT;
      };

      const isDentalCheckboxEnabled = () => {
        if (!formStateDentalRow(0)?.plan_id) {
          return false;
        }
        return member.dentalCoverage?.plan_is_editable;
      };

      const isDentalCheckboxChecked = () => {
        return Boolean(formStateDentalRow(index)?.plan_id);
      };

      function handleDentalPlanSelect(event: ChangeEvent<HTMLInputElement>) {
        const selectedPlan = dentalPlans.find((plan: Plan) => plan.id == event.target.value);
        const selectedPlanId = selectedPlan?.id ?? null;
        const selectedPlanType = selectedPlan?.type ?? null;

        const newEditEnrollment = (
          vision: boolean,
          editEnrollment: EditEnrollment
        ): EditEnrollment => {
          const planId = () => {
            switch (vision) {
              case true:
                return selectedPlanId ? visionPlans[0].id : null;
              case false:
                return selectedPlanId;
            }
          };
          const planType = () => {
            switch (vision) {
              case true:
                return selectedPlanId ? visionPlans[0].type : null;
              case false:
                return selectedPlanType;
            }
          };
          const newEditEnrollment: EditEnrollment = {
            ...editEnrollment,
            plan_id: planId(),
            plan_type: planType(),
            plan_state:
              !event.target.value || event.target.value === NO_DENTAL_VALUE
                ? EnrollmentStates.NotEnrolled
                : EnrollmentStates.Enrolled,
            coverage_start_date_error: '',
          };
          if (!selectedPlanId) {
            newEditEnrollment.coverage_start_date = null;
          } else {
            newEditEnrollment.remove_coverage = false;
          }
          return newEditEnrollment;
        };

        const newFamilyCoveragesDental = [...formState?.family_coverages_dental!];
        newFamilyCoveragesDental.forEach((editEnrollment: EditEnrollment, i: number) => {
          newFamilyCoveragesDental.splice(i, 1, newEditEnrollment(false, editEnrollment));
        });
        const newFormState: EditEnrollmentsPayload = {
          primary_coverage_dental: newEditEnrollment(false, formState?.primary_coverage_dental!),
          family_coverages_dental: newFamilyCoveragesDental,
        };

        const newFamilyCoveragesVision = [...formState?.family_coverages_vision!];
        if (cloneDentalToVisionSettings) {
          newFormState.primary_coverage_vision = newEditEnrollment(
            true,
            formState?.primary_coverage_vision!
          );

          newFamilyCoveragesVision.forEach((familyCoverage: EditEnrollment, i: number) => {
            newFamilyCoveragesVision.splice(i, 1, newEditEnrollment(true, familyCoverage));
          });
          newFormState.family_coverages_vision = newFamilyCoveragesVision;
        }
        setFormState(newFormState);
      }

      const handleCheckboxChange =
        (vision: boolean): ((event: ChangeEvent<HTMLInputElement>) => void) =>
        (event: ChangeEvent<HTMLInputElement>) => {
          if (vision && cloneDentalToVisionSettings) return;

          if (vision && index === 0) {
            const primaryDate = formStateVisionRow(0)?.coverage_start_date;

            const newFamilyCoveragesVision = [...formState?.family_coverages_vision!];
            const selectedPlan = event.target.checked ? visionPlans[0] : null;
            const selectedPlanId = selectedPlan?.id ?? null;
            const selectedPlanType = selectedPlan?.type ?? null;

            const newEditEnrollment = (editEnrollment: EditEnrollment): EditEnrollment => {
              const newEditEnrollment: EditEnrollment = {
                ...editEnrollment,
                plan_id: selectedPlanId,
                plan_type: selectedPlanType,
                plan_state: event.target.checked
                  ? EnrollmentStates.Enrolled
                  : EnrollmentStates.NotEnrolled,
                remove_coverage: !event.target.checked && Boolean(editEnrollment.coverage_id),
                coverage_start_date: event.target.checked
                  ? primaryDate
                    ? dayjs.max(dayjs(primaryDate), dayjs())?.format('MM/DD/YYYY')
                    : null
                  : null,
                coverage_start_date_error: '',
              };
              return newEditEnrollment;
            };

            newFamilyCoveragesVision.forEach((editEnrollment: EditEnrollment, i: number) => {
              newFamilyCoveragesVision.splice(i, 1, newEditEnrollment(editEnrollment));
            });

            setFormState({
              primary_coverage_vision: newEditEnrollment(formState?.primary_coverage_vision!),
              family_coverages_vision: newFamilyCoveragesVision,
            });

            return;
          }

          const primaryRow = vision ? formStateVisionRow(0) : formStateDentalRow(0);
          const familyCurrentRow = vision
            ? formState?.family_coverages_vision![index - 1]
            : formState?.family_coverages_dental![index - 1];
          const primaryDate = primaryRow?.coverage_start_date;
          const newFamilyCoverage = {
            ...familyCurrentRow,
            plan_id: event.target.checked ? primaryRow?.plan_id : null,
            plan_type: event.target.checked ? primaryRow?.plan_type : null,
            plan_state: event.target.checked
              ? EnrollmentStates.Enrolled
              : EnrollmentStates.NotEnrolled,
            remove_coverage: !event.target.checked && Boolean(familyCurrentRow?.coverage_id),
            coverage_start_date: event.target.checked
              ? primaryDate
                ? dayjs.max(dayjs(primaryDate), dayjs())?.format('MM/DD/YYYY')
                : null
              : null,
            coverage_start_date_error: '',
          };
          const newFamilyCoverages = vision
            ? [...formState?.family_coverages_vision!]
            : [...formState?.family_coverages_dental!];

          newFamilyCoverages.splice(index - 1, 1, newFamilyCoverage);

          const newFormState: EditEnrollmentsPayload = {
            [`family_coverages_${vision ? 'vision' : 'dental'}`]: newFamilyCoverages,
          };
          if (!vision && cloneDentalToVisionSettings) {
            const newFamilyCoveragesVision = [...formState?.family_coverages_vision!];
            const newFamilyCoverageVision = {
              ...formState?.family_coverages_vision![index - 1],
              plan_id: event.target.checked ? visionPlans[0]?.id : null,
              plan_type: event.target.checked ? visionPlans[0]?.type : null,
              plan_state: event.target.checked
                ? EnrollmentStates.Enrolled
                : EnrollmentStates.NotEnrolled,
              coverage_start_date: event.target.checked
                ? primaryDate
                  ? dayjs.max(dayjs(primaryDate), dayjs())?.format('MM/DD/YYYY')
                  : null
                : null,
              coverage_start_date_error: '',
            };
            newFamilyCoveragesVision.splice(index - 1, 1, newFamilyCoverageVision);
            newFormState.family_coverages_vision = newFamilyCoveragesVision;
          }
          setFormState(newFormState);
        };

      const handleStartDateChange =
        (vision: boolean): ((event: ChangeEvent<HTMLInputElement>) => void) =>
        (event: ChangeEvent<HTMLInputElement>) => {
          if (vision && cloneDentalToVisionSettings) return;

          const newDateEditEnrollment = (editEnrollment: EditEnrollment): EditEnrollment => {
            const newDateEditEnrollment = {
              ...editEnrollment,
              coverage_start_date: event.target.value,
              coverage_start_date_error: '',
            };
            const plans = vision ? visionPlans : dentalPlans;
            if (
              !(vision ? formStateVisionRow(index) : formStateDentalRow(index))?.plan_id &&
              plans?.length === 1
            ) {
              newDateEditEnrollment.plan_id = plans[0].id;
              newDateEditEnrollment.plan_type = plans[0].type;
              newDateEditEnrollment.remove_coverage = false;
            }
            return newDateEditEnrollment;
          };

          const newFamilyCoverages = vision
            ? [...formState?.family_coverages_vision!]
            : [...formState?.family_coverages_dental!];
          if (index === 0) {
            newFamilyCoverages.forEach((editEnrollment: EditEnrollment, i: number) => {
              const primaryCoverageStartDate = dayjs(event.target.value);
              const familyMemberCoverageStartDate = dayjs(editEnrollment.coverage_start_date);
              const isMemberDateLessThanPrimaryDate = primaryCoverageStartDate.isAfter(
                familyMemberCoverageStartDate
              );
              if (
                isMemberDateLessThanPrimaryDate ||
                (!editEnrollment.coverage_start_date && editEnrollment.plan_id)
              ) {
                newFamilyCoverages.splice(i, 1, newDateEditEnrollment(editEnrollment));
              }
            });
            const newFormState: EditEnrollmentsPayload = {
              [`primary_coverage_${vision ? 'vision' : 'dental'}`]: newDateEditEnrollment(
                vision ? formState?.primary_coverage_vision! : formState?.primary_coverage_dental!
              ),
              [`family_coverages_${vision ? 'vision' : 'dental'}`]: newFamilyCoverages,
            };
            if (!vision && cloneDentalToVisionSettings) {
              const newFamilyCoveragesVision = [...formState?.family_coverages_vision!];
              newFamilyCoveragesVision.forEach((editEnrollment: EditEnrollment, i: number) => {
                const primaryCoverageStartDate = dayjs(event.target.value);
                const familyMemberCoverageStartDate = dayjs(editEnrollment.coverage_start_date);
                const isMemberDateLessThanPrimaryDate = primaryCoverageStartDate.isAfter(
                  familyMemberCoverageStartDate
                );
                if (
                  editEnrollment.plan_id &&
                  (isMemberDateLessThanPrimaryDate || !editEnrollment.coverage_start_date)
                ) {
                  newFamilyCoveragesVision.splice(i, 1, newDateEditEnrollment(editEnrollment));
                }
              });
              newFormState.primary_coverage_vision = newDateEditEnrollment(
                formState?.primary_coverage_vision!
              );
              newFormState.family_coverages_vision = newFamilyCoveragesVision;
            }
            setFormState(newFormState);
          } else {
            newFamilyCoverages.splice(
              index - 1,
              1,
              newDateEditEnrollment(
                vision ? formStateVisionRow(index)! : formStateDentalRow(index)!
              )
            );
            const newFormState: EditEnrollmentsPayload = {
              [`family_coverages_${vision ? 'vision' : 'dental'}`]: newFamilyCoverages,
            };
            if (!vision && cloneDentalToVisionSettings) {
              const newFamilyCoveragesVision = [...formState?.family_coverages_vision!];
              newFamilyCoveragesVision.splice(
                index - 1,
                1,
                newDateEditEnrollment(formStateVisionRow(index)!)
              );
              newFormState.family_coverages_vision = newFamilyCoveragesVision;
            }
            setFormState(newFormState);
          }
        };

      const handleEndDateChange =
        (vision: boolean): ((event: ChangeEvent<HTMLInputElement>) => void) =>
        (event: ChangeEvent<HTMLInputElement>) => {
          if (vision && cloneDentalToVisionSettings) return;

          const newDateEditEnrollment = (editEnrollment: EditEnrollment): EditEnrollment => {
            const newDateEditEnrollment = {
              ...editEnrollment,
              coverage_end_date: event.target.value,
              coverage_end_date_error: '',
            };
            if (event.target.value) {
              newDateEditEnrollment.undo_cancellation = false;
              newDateEditEnrollment.plan_state = EnrollmentStates.Covered2;
            }
            return newDateEditEnrollment;
          };
          if (index === 0) {
            const newFamilyCoverages = vision
              ? [...formState?.family_coverages_vision!]
              : [...formState?.family_coverages_dental!];
            newFamilyCoverages.forEach((editEnrollment: EditEnrollment, i: number) => {
              const primaryCoverageEndDate = dayjs(event.target.value);
              const familyMemberCoverageEndDate = dayjs(editEnrollment.coverage_end_date);
              const isMemberDateGreaterThanPrimaryDate =
                familyMemberCoverageEndDate.isAfter(primaryCoverageEndDate);
              if (
                isMemberDateGreaterThanPrimaryDate ||
                (!editEnrollment.coverage_end_date && editEnrollment.plan_id)
              ) {
                newFamilyCoverages.splice(i, 1, newDateEditEnrollment(editEnrollment));
              }
            });
            const newFormState: EditEnrollmentsPayload = {
              [`primary_coverage_${vision ? 'vision' : 'dental'}`]: newDateEditEnrollment(
                vision ? formState?.primary_coverage_vision! : formState?.primary_coverage_dental!
              ),
              [`family_coverages_${vision ? 'vision' : 'dental'}`]: newFamilyCoverages,
            };

            if (!vision && cloneDentalToVisionSettings) {
              const newFamilyCoveragesVision = [...formState?.family_coverages_vision!];
              newFamilyCoveragesVision.forEach((editEnrollment: EditEnrollment, i: number) => {
                const primaryCoverageEndDate = dayjs(event.target.value);
                const familyMemberCoverageEndDate = dayjs(editEnrollment.coverage_start_date);
                const isMemberDateGreaterThanPrimaryDate =
                  familyMemberCoverageEndDate.isAfter(primaryCoverageEndDate);
                if (
                  isMemberDateGreaterThanPrimaryDate ||
                  (!editEnrollment.coverage_end_date && editEnrollment.plan_id)
                ) {
                  newFamilyCoveragesVision.splice(i, 1, newDateEditEnrollment(editEnrollment));
                }
              });
              newFormState.primary_coverage_vision = newDateEditEnrollment(
                formState?.primary_coverage_vision!
              );
              newFormState.family_coverages_vision = newFamilyCoveragesVision;
            }

            setFormState(newFormState);
          } else {
            const newFamilyCoverages = vision
              ? [...formState?.family_coverages_vision!]
              : [...formState?.family_coverages_dental!];
            newFamilyCoverages.splice(
              index - 1,
              1,
              newDateEditEnrollment(
                vision ? formStateVisionRow(index)! : formStateDentalRow(index)!
              )
            );
            const newFormState: EditEnrollmentsPayload = {
              [`family_coverages_${vision ? 'vision' : 'dental'}`]: newFamilyCoverages,
            };
            if (!vision && cloneDentalToVisionSettings) {
              const newFamilyCoveragesVision = [...formState?.family_coverages_vision!];
              newFamilyCoveragesVision.splice(
                index - 1,
                1,
                newDateEditEnrollment(formStateVisionRow(index)!)
              );
              newFormState.family_coverages_vision = newFamilyCoveragesVision;
            }
            setFormState(newFormState);
          }
        };

      const minCoverageStartDate = (vision: boolean) => {
        const primaryDate = dayjs(
          (vision ? formStateVisionRow(0) : formStateDentalRow(0))?.coverage_start_date
        );
        const currentMemberMaxDate = dayjs(
          (vision ? member.visionCoverage : member.dentalCoverage)?.min_coverage_start_date
        );
        return primaryDate.isValid() && index > 0
          ? dayjs.max(primaryDate, currentMemberMaxDate)
          : currentMemberMaxDate;
      };

      const maxCoverageEndDate = (vision: boolean) => {
        const primaryDate = dayjs(
          (vision ? formStateVisionRow(0) : formStateDentalRow(0))?.coverage_end_date
        );
        const currentMemberMaxDate = dayjs(
          (vision ? member.visionCoverage : member.dentalCoverage)?.max_coverage_end_date
        );
        return primaryDate.isValid() && index > 0
          ? dayjs.min(primaryDate, currentMemberMaxDate)
          : currentMemberMaxDate;
      };

      const isDentalStartDateEnabled = () => {
        return (
          member.dentalCoverage?.coverage_start_date_is_editable &&
          formStateDentalRow(index)?.plan_id
        );
      };

      const isVisionStartDateEnabled = () => {
        if (cloneDentalToVisionSettings) {
          return false;
        }
        return (
          member.visionCoverage?.coverage_start_date_is_editable &&
          formStateVisionRow(index)?.plan_id
        );
      };

      const isVisionCheckboxEnabled = () => {
        if (cloneDentalToVisionSettings) {
          return false;
        }
        if (index > 0 && !formStateVisionRow(0)?.plan_id) {
          return false;
        }
        return member.visionCoverage?.plan_is_editable;
      };

      const isVisionCheckboxChecked = () => {
        if (cloneDentalToVisionSettings) {
          return isDentalCheckboxChecked();
        }
        return Boolean(formStateVisionRow(index)?.plan_id);
      };

      const undoCancellation = (vision: boolean) => {
        const primaryFormStateRow = vision ? formStateVisionRow(0) : formStateDentalRow(0);

        const newEditEnrollment = (vision: boolean, i: number) => ({
          ...(vision ? formStateVisionRow(i) : formStateDentalRow(i)),
          plan_state: EnrollmentStates.Covered1,
          coverage_end_date: null,
          undo_cancellation: true,
        });
        const newFamilyCoverages = vision
          ? [...formState?.family_coverages_vision!]
          : [...formState?.family_coverages_dental!];

        if (index === 0) {
          newFamilyCoverages.forEach((editEnrollment: EditEnrollment, i: number) => {
            const primaryDate = primaryFormStateRow?.coverage_end_date;
            const familyMemberDate = editEnrollment.coverage_end_date;
            if (primaryDate == familyMemberDate) {
              newFamilyCoverages.splice(i, 1, newEditEnrollment(vision, i + 1));
            }
          });

          const newFormState: EditEnrollmentsPayload = {
            [`primary_coverage_${vision ? 'vision' : 'dental'}`]: newEditEnrollment(vision, 0),
            [`family_coverages_${vision ? 'vision' : 'dental'}`]: newFamilyCoverages,
          };

          if (!vision && cloneDentalToVisionSettings) {
            const newFamilyCoveragesVision = [...formState?.family_coverages_vision!];
            newFamilyCoveragesVision.forEach((editEnrollment: EditEnrollment, i: number) => {
              const primaryDate = primaryFormStateRow?.coverage_end_date;
              const familyMemberDate = editEnrollment.coverage_end_date;
              if (primaryDate == familyMemberDate) {
                newFamilyCoveragesVision.splice(i, 1, newEditEnrollment(true, i + 1));
              }
            });
            newFormState.primary_coverage_vision = newEditEnrollment(true, 0);
            newFormState.family_coverages_vision = newFamilyCoveragesVision;
          }
          setFormState(newFormState);
        } else {
          newFamilyCoverages.splice(index - 1, 1, newEditEnrollment(vision, index));

          const newFormState: EditEnrollmentsPayload = {
            [`family_coverages_${vision ? 'vision' : 'dental'}`]: newFamilyCoverages,
          };
          if (!vision && cloneDentalToVisionSettings) {
            const newFamilyCoveragesVision = [...formState?.family_coverages_vision!];
            newFamilyCoveragesVision.splice(index - 1, 1, newEditEnrollment(true, index));
            newFormState.family_coverages_vision = newFamilyCoveragesVision;
          }
          setFormState(newFormState);
        }
      };

      const cancelCoverage = (vision: boolean) => {
        const newEditEnrollment = (vision: boolean, n: number) => ({
          ...(vision ? formStateVisionRow(n) : formStateDentalRow(n)),
          plan_state: EnrollmentStates.Covered2,
          undo_cancellation: false,
        });
        if (index === 0) {
          const newFormState: EditEnrollmentsPayload = {
            [`primary_coverage_${vision ? 'vision' : 'dental'}`]: newEditEnrollment(vision, 0),
          };

          if (!vision && cloneDentalToVisionSettings) {
            newFormState.primary_coverage_vision = newEditEnrollment(true, 0);
          }
          setFormState(newFormState);
        } else {
          const newFamilyCoverages = vision
            ? [...formState?.family_coverages_vision!]
            : [...formState?.family_coverages_dental!];

          newFamilyCoverages.splice(index - 1, 1, newEditEnrollment(vision, index));

          const newFormState: EditEnrollmentsPayload = {
            [`family_coverages_${vision ? 'vision' : 'dental'}`]: newFamilyCoverages,
          };
          if (!vision && cloneDentalToVisionSettings) {
            const newFamilyCoveragesVision = [...formState?.family_coverages_vision!];
            newFamilyCoveragesVision.splice(index - 1, 1, newEditEnrollment(true, index));
            newFormState.family_coverages_vision = newFamilyCoveragesVision;
          }
          setFormState(newFormState);
        }
      };

      const removeCoverage = (vision: boolean) => {
        const newEditEnrollment = (vision: boolean, n: number) => {
          const currentFormStateRow = vision ? formStateVisionRow(n) : formStateDentalRow(n);
          return {
            ...currentFormStateRow,
            plan_state: EnrollmentStates.NotEnrolled,
            coverage_start_date: null,
            plan_id: null,
            plan_type: null,
            remove_coverage: Boolean(currentFormStateRow?.coverage_id),
          };
        };
        if (index === 0) {
          const newFamilyCoverages = vision
            ? [...formState?.family_coverages_vision!]
            : [...formState?.family_coverages_dental!];
          newFamilyCoverages.forEach((editEnrollment: EditEnrollment, i: number) => {
            newFamilyCoverages.splice(i, 1, newEditEnrollment(vision, i + 1));
          });
          const newFormState: EditEnrollmentsPayload = {
            [`primary_coverage_${vision ? 'vision' : 'dental'}`]: newEditEnrollment(vision, 0),
            [`family_coverages_${vision ? 'vision' : 'dental'}`]: newFamilyCoverages,
          };
          if (!vision && cloneDentalToVisionSettings) {
            const newFamilyCoveragesVision = [...formState?.family_coverages_vision!];
            newFamilyCoveragesVision.forEach((editEnrollment: EditEnrollment, i: number) => {
              newFamilyCoveragesVision.splice(i, 1, newEditEnrollment(true, i + 1));
            });
            newFormState.primary_coverage_vision = newEditEnrollment(true, 0);
            newFormState.family_coverages_vision = newFamilyCoveragesVision;
          }
          setFormState(newFormState);
        } else {
          const newFamilyCoverages = vision
            ? [...formState?.family_coverages_vision!]
            : [...formState?.family_coverages_dental!];
          newFamilyCoverages.splice(index - 1, 1, newEditEnrollment(vision, index));

          const newFormState: EditEnrollmentsPayload = {
            [`family_coverages_${vision ? 'vision' : 'dental'}`]: newFamilyCoverages,
          };
          if (!vision && cloneDentalToVisionSettings) {
            const newFamilyCoveragesVision = [...formState?.family_coverages_vision!];
            newFamilyCoveragesVision.splice(index - 1, 1, newEditEnrollment(true, index));
            newFormState.family_coverages_vision = newFamilyCoveragesVision;
          }
          setFormState(newFormState);
        }
      };

      const planOptions = dentalPlans.map((plan: Plan) => (
        <MenuItem key={`dental-plans-${index}-${plan.id}`} value={plan.id}>
          {plan.label}
        </MenuItem>
      ));
      const noDentalMenuItem = (
        <MenuItem key={`dental-plans-${index}-no-dental`} value={NO_DENTAL_VALUE}>
          {NO_DENTAL_TEXT}
        </MenuItem>
      );

      const isPrimaryDentalInitiallyEnrolled = Boolean(members[0].dentalCoverage.plan_id);

      if (!isPrimaryDentalInitiallyEnrolled || !formStateDentalRow(0)?.plan_id) {
        planOptions.splice(0, 0, noDentalMenuItem);
      }

      const dentalPlanCell =
        index === 0 ? (
          <Stack direction="column" alignItems="center" width={160}>
            <OutlinedTextField
              select
              id="dental-plan-select"
              name="plan"
              value={formStateDentalRow(0)?.plan_id ?? NO_DENTAL_VALUE}
              disabled={!members[0].dentalCoverage?.plan_is_editable}
              onChange={handleDentalPlanSelect}
            >
              {planOptions}
            </OutlinedTextField>
          </Stack>
        ) : (
          <Stack direction="row" spacing={0} width={160}>
            <Checkbox
              id={`dental-coverage-${index}`}
              name={`dental-coverage-${index}`}
              checked={isDentalCheckboxChecked()}
              onChange={handleCheckboxChange(false)}
              disabled={!isDentalCheckboxEnabled()}
            />
            <Typography>{planText()}</Typography>
          </Stack>
        );

      const planStatusCell = (planState: EnrollmentState, vision: boolean) => {
        const currentFormStateRow = vision ? formStateVisionRow(index) : formStateDentalRow(index);
        const currentCoverageRow = vision ? member.visionCoverage : member.dentalCoverage;
        const isStartDateEnabled = vision ? isVisionStartDateEnabled() : isDentalStartDateEnabled();
        const isCobra = currentCoverageRow?.is_cobra;
        switch (planState) {
          case EnrollmentStates.NotEnrolled:
            // 'Starts coverage on: <date_control>'
            return (
              <Box width={240}>
                <DatePickerField
                  id={`${vision ? 'vision' : 'dental'}-start-date-of-coverage-${index}`}
                  name={`${vision ? 'vision' : 'dental'}-start-date-of-coverage-${index}`}
                  label="Starts coverage on"
                  onChange={handleStartDateChange(vision)}
                  value={formatDate(currentFormStateRow?.coverage_start_date, 'MM/DD/YYYY')}
                  minDate={formatDate(minCoverageStartDate(vision), 'MM/DD/YYYY')}
                  maxDate={formatDate(currentCoverageRow?.max_coverage_start_date, 'MM/DD/YYYY')}
                  disabled={!isStartDateEnabled}
                  error={Boolean(currentFormStateRow?.coverage_start_date_error)}
                />
              </Box>
            );
          case EnrollmentStates.Enrolled:
            // 'Coverage starts on: <date_control>'
            // 'Action: <Remove_coverage>
            return (
              <Stack direction="column" spacing="0" width={240}>
                <DatePickerField
                  id={`${vision ? 'vision' : 'dental'}-start-date-of-coverage-${index}`}
                  name={`${vision ? 'vision' : 'dental'}-start-date-of-coverage-${index}`}
                  label={`${isCobra ? 'COBRA coverage starts on' : 'Coverage starts on'}`}
                  onChange={handleStartDateChange(vision)}
                  value={formatDate(currentFormStateRow?.coverage_start_date, 'MM/DD/YYYY')}
                  minDate={formatDate(minCoverageStartDate(vision), 'MM/DD/YYYY')}
                  maxDate={formatDate(currentCoverageRow?.max_coverage_start_date, 'MM/DD/YYYY')}
                  disabled={!isStartDateEnabled}
                  error={Boolean(currentFormStateRow?.coverage_start_date_error)}
                />
                <TableButton
                  id={`dental-remove-coverage-button-${index}`}
                  onClick={() => removeCoverage(vision)}
                  sx={{ marginBottom: -2 }}
                  disabled={vision && cloneDentalToVisionSettings}
                >
                  Remove coverage
                </TableButton>
              </Stack>
            );
          case EnrollmentStates.Covered1:
            // 'Covered since: <date_label>
            // 'Action: <Cancel_coverage>
            return (
              <Stack direction="column" spacing="0" width={240}>
                <Typography fontSize={13}>
                  {isCobra ? 'COBRA covered since' : 'Covered since'}:{' '}
                  {formatDate(currentFormStateRow?.coverage_start_date, 'MM/DD/YYYY')}
                </Typography>
                <TableButton
                  id={`dental-cancel-coverage-button-${index}`}
                  onClick={() => cancelCoverage(vision)}
                  sx={{ marginBottom: -2 }}
                  disabled={vision && cloneDentalToVisionSettings}
                >
                  Cancel coverage
                </TableButton>
              </Stack>
            );
          case EnrollmentStates.Covered2:
          default:
            // 'Covered since: <date_label>
            // 'Will be cancelled effective: <date_control>
            // 'Action: <Remove_cancellation>
            const primaryFormStateRow = vision ? formStateVisionRow(0) : formStateDentalRow(0);
            return (
              <Stack direction="column" spacing="0" width={240}>
                <Typography fontSize={13} mb={1.5}>
                  {isCobra ? 'COBRA covered since' : 'Covered since'}:{' '}
                  {formatDate(currentFormStateRow?.coverage_start_date, 'MM/DD/YYYY')}
                </Typography>
                <DatePickerField
                  id={`dental-end-date-of-coverage-${index}`}
                  name="dentalCoverageEndDate"
                  label="Final day of coverage"
                  onChange={handleEndDateChange(vision)}
                  value={formatDate(currentFormStateRow?.coverage_end_date, 'MM/DD/YYYY')}
                  minDate={formatDate(currentCoverageRow?.min_coverage_end_date, 'MM/DD/YYYY')}
                  maxDate={formatDate(maxCoverageEndDate(vision), 'MM/DD/YYYY')}
                  disabled={vision && cloneDentalToVisionSettings}
                  error={Boolean(currentFormStateRow?.coverage_end_date_error)}
                />
                {(index === 0 ||
                  (primaryFormStateRow?.plan_state === EnrollmentStates.Covered1 &&
                    !primaryFormStateRow?.coverage_end_date)) && (
                  <TableButton
                    id={`dental-remove-cancellation-button-${index}`}
                    onClick={() => undoCancellation(vision)}
                    sx={{ marginBottom: -2 }}
                    disabled={vision && cloneDentalToVisionSettings}
                  >
                    Undo cancellation
                  </TableButton>
                )}
              </Stack>
            );
        }
      };

      const cells: (string | JSX.Element)[] = [
        member.dentalCoverage.name,
        member.dentalCoverage.relationship,
        <NoWrap>{formatMemberId(member.dentalCoverage.member_id)}</NoWrap>,
        dentalPlanCell,
        planStatusCell(formStateDentalRow(index)?.plan_state!, false),
      ];

      if (showVisionPart) {
        const visionPlanCell = (
          <Stack direction="column" alignItems="center" width={60}>
            <Checkbox
              id={`vision-coverage-${index}`}
              key={`vision-coverage-${index}`}
              name={`vision-coverage-${index}`}
              checked={isVisionCheckboxChecked()}
              onChange={handleCheckboxChange(true)}
              disabled={!isVisionCheckboxEnabled() || cloneDentalToVisionSettings}
            />
          </Stack>
        );
        cells.splice(
          5,
          0,
          visionPlanCell,
          planStatusCell(formStateVisionRow(index)?.plan_state!, true)
        );
      }

      return makeRow('coverage-table-row', cells);
    });

    return <StandardTable id="coverage-table" headers={headers} rows={rows} />;
  };

  return (
    <ConfirmDialog
      open={open}
      onClose={onClose}
      dialogName="enroll-dialog"
      scroll="body"
      fullWidth={false}
      maxWidth="lg"
      title={'Dental and vision coverage'}
      confirmText={'Update'}
      onConfirm={async () => {
        if (canSubmit() && formState) {
          bulkEnrollEmployee(formState)
            .then(() => onClose())
            .catch((error: Error) => {
              notify(error.message, 'error');
            });
        }
      }}
    >
      {showLoader ? (
        <Loader size="sm" />
      ) : (
        <>
          <Stack direction="row" spacing={15}>
            <Typography>Plan year: {planYear?.label}</Typography>
            {coverage && coverage.show_dental_and_vision_together_button && (
              <FormControlLabel
                id="clone-detal-to-vision"
                control={
                  <Checkbox
                    checked={cloneDentalToVisionSettings}
                    onChange={(event: ChangeEvent<HTMLInputElement>) => {
                      if (formState?.primary_coverage_vision) {
                        copyPrimaryDentalToVisionState();
                        if (formState?.family_coverages_vision) {
                          copyFamilyDentalToVisionState();
                        }
                      }
                      setCloneDentalToVisionSettings(!cloneDentalToVisionSettings);
                    }}
                  />
                }
                label="Use the same dates for dental and vision"
              />
            )}
          </Stack>
          {displayForm()}
        </>
      )}
    </ConfirmDialog>
  );
}
